import { Box, Paper, Typography, Button, IconButton } from "@mui/material";
import { FiMoreVertical } from "react-icons/fi";
import projectDefaultImg from "../../../assets/categories/project_default.png";

const ProjectCard = ({ project, resolvedImage, onAction, actionLabel, isDark, tok, isSelected = false, onClick, onMenuClick, isTemplate = false }) => {

  const resolveProjectImage = () => {
    const candidates = [
      project?.material_photo,
      project?.photo_url,
      project?.photo,
      project?.image_url,
      project?.image,
      project?.thumbnail,
      project?.project_photo,
      project?.workspace_photo,
      project?.workspace_photo_url,
    ];

    for (const value of candidates) {
      if (!value) continue;
      if (typeof value === "string" && value.trim()) return value;
      if (typeof value === "object") {
        if (typeof value.url === "string" && value.url.trim()) return value.url;
        if (typeof value.secure_url === "string" && value.secure_url.trim()) return value.secure_url;
        if (typeof value.path === "string" && value.path.trim()) return value.path;
      }
    }

    return "";
  };

  const brandColor = tok?.bg?.tabActive || "#659d33";

  const formattedDate = project.date_modified 
    ? new Date(project.date_modified).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : "30 Jul 2026";

  const displayGwp = project.gwp !== undefined && project.gwp !== null
    ? `${Number(project.gwp).toFixed(2)} kg CO₂e`
    : "0.00 kg CO₂e";

  const functionalUnit = project.functional_unit === "other"
    ? (project.other_unit || "").trim()
    : (project.functional_unit || "").trim();

  const mappedImage = resolveProjectImage();
  const cardImage = resolvedImage || mappedImage || projectDefaultImg;

  return (
    <Paper
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "320px",
        overflow: "hidden",
        position: "relative",
        bgcolor: tok.bg.card,
        border: isSelected ? "2px solid" : "1px solid",
        borderColor: isSelected ? brandColor : tok.border.card,
        borderRadius: tok.radius.card,
        boxShadow: isSelected ? `0 0 12px rgba(101, 157, 51, 0.4)` : tok.shadow.card,
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isSelected ? `0 0 16px rgba(101, 157, 51, 0.6)` : tok.shadow.cardHover,
        },
      }}
    >
      <Box
        sx={{
          height: "140px",
          width: "100%",
          backgroundImage: `url(${cardImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.15)" }} />
        
        {/* 3-dots Context Menu Overlay */}
        {onMenuClick && !isTemplate && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e, project);
            }}
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              bgcolor: isDark ? "rgba(30, 41, 59, 0.7)" : "rgba(255, 255, 255, 0.8)",
              color: isDark ? "#fff" : "#1e293b",
              padding: "6px",
              "&:hover": {
                bgcolor: isDark ? "rgba(30, 41, 59, 0.9)" : "rgba(255, 255, 255, 0.95)",
              }
            }}
          >
            <FiMoreVertical style={{ fontSize: "16px" }} />
          </IconButton>
        )}
        
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", flexGrow: 1, justifyContent: "space-between", minHeight: 0 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, textAlign: "left" }}>
          <Typography
            sx={{
              fontFamily: "Poppins",
              fontSize: "15px",
              fontWeight: 700,
              color: tok.text.primary,
              lineHeight: 1.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              textAlign: "left"
            }}
          >
            {project.name}
          </Typography>
          <Typography sx={{
            fontFamily: tok?.font?.family || "Poppins, sans-serif",
            fontSize: tok?.font?.size?.gridHeader || "14px",
            fontWeight: tok?.font?.weight?.medium || 500,
            color: isDark ? "rgba(255,255,255,0.72)" : "#475569",
            textAlign: "left"
          }}>
            {project.location || "Global"}
          </Typography>
        </Box>

        {functionalUnit && (
          <Typography sx={{
            fontFamily: tok?.font?.family || "Poppins, sans-serif",
            fontSize: tok?.font?.size?.gridHeader || "14px",
            fontWeight: tok?.font?.weight?.medium || 500,
            color: isDark ? "rgba(255,255,255,0.72)" : "#475569",
            textAlign: "left",
            mt: 1,
            mb: 0.5,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}>
            Functional Unit: {functionalUnit}
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: functionalUnit ? 1 : 2, borderTop: "1px solid", borderColor: tok.border.divider, pt: 1.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
            <Typography sx={{ fontSize: "11px", color: isDark ? "rgba(255,255,255,0.5)" : "#94a3b8", textAlign: "left" }}>
              {isTemplate ? "Standard Template" : formattedDate}
            </Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isDark ? "rgba(255,255,255,0.7)" : "#475569", textAlign: "left" }}>
              {project.year || "2026"}
            </Typography>
          </Box>

          {isTemplate && onAction ? (
            <Button
              variant="outlined"
              onClick={(e) => {
                e.stopPropagation();
                onAction(project);
              }}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "13px",
                fontFamily: "Poppins",
                borderColor: brandColor,
                color: brandColor,
                "&:hover": {
                  borderColor: brandColor,
                  bgcolor: isDark ? "rgba(101, 157, 51, 0.08)" : "rgba(101, 157, 51, 0.04)",
                },
              }}
            >
              {actionLabel || "Use Template"}
            </Button>
          ) : (
            <Box
              sx={{
                borderRadius: "8px",
                border: "1px solid",
                borderColor: brandColor,
                color: brandColor,
                px: 1.5,
                py: 0.6,
                fontWeight: 700,
                fontSize: "13px",
                fontFamily: "Poppins",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isDark ? "rgba(101, 157, 51, 0.04)" : "rgba(101, 157, 51, 0.02)",
              }}
            >
              {displayGwp}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ProjectCard;
